<script lang="ts" setup>
import { ref, type ComponentPublicInstance, type Ref } from 'vue';
import InputTextBase from './InputTextBase.vue';
import { useValidator } from '@/composeables';
import type { IEditorComponent } from '@/objects/gui';
import { ValidatorMessage, type IInputTextModelValueType, type IInputTextPropsWithoutModelValue } from '@/components';
import { computed } from 'vue';
import type { IEmitBlur } from '../Interfaces/IEmitBlur';
import type { IEmitFocus } from '../Interfaces/IEmitFocus';

const props = withDefaults(defineProps<IInputTextPropsWithoutModelValue>(), {
    placeholder: '',
    validator: null,
    labelPosition: 'left',
    multiline: false,
    pattern: '.*',
    selectOnFocus: false,
    readonly: false,
});

const modelValue = defineModel<IInputTextModelValueType>({
    required: true,
});

const inputref = ref<ComponentPublicInstance<IEditorComponent> | null>(null);

const hasHadFocus = ref(false);

const onFocus = (ev: Event) => {
    emits('focus', ev);
};

const onBlur = (ev: Event) => {
    hasHadFocus.value = true;

    emits('blur', ev);
};

/**
 * This is raised when the value changes.
 * @param newvalue
 * @param oldvalue
 */
const onChange = (newvalue: string | null, oldvalue: string | null) => {
    if (newvalue) {
        Validate(newvalue);
    }
};

const emits = defineEmits<
    {
        (e: 'update:modelValue', value: string): void;
        (e: 'patternMismatch', event: Event): void;
    } & IEmitBlur &
        IEmitFocus
>();

/**
 * The value does the validation and converts the modelValue to a string (it can be null here, but not in the base)
 *
 * TODO: The validation should be moved into the base component
 */
const value = computed({
    get: () => modelValue.value ?? '',
    set: (value) => {
        modelValue.value = value;
        if (!Validate(value)) return '';
    },
});

// Initialize the validator
const { Validate, ValidateMessage } = useValidator(props.validator, value.value)!;

/**
 * Define the exposed methods
 */
defineExpose<IEditorComponent>({
    focus: () => inputref.value?.focus(),
    validateMessage: ValidateMessage,
    KeyboardEvent: (ev: KeyboardEvent) => {
        value.value = ev.key;
    },
});
</script>

<template>
    <ValidatorMessage
        :message="ValidateMessage"
        :hide="!hasHadFocus"
    >
        <div
            class="d-flex"
            :class="{ 'flex-column': props.labelPosition == 'top' }"
        >
            <InputTextBase
                v-if="!props.multiline"
                ref="inputref"
                v-model="value"
                :id="props.id"
                :placeholder="props.placeholder"
                :pattern="props.pattern"
                :selectOnFocus="props.selectOnFocus"
                @patternMismatch="emits('patternMismatch', $event)"
                @focus="onFocus"
                @blur="onBlur"
                @change="onChange"
                type="text"
                :class="[props.labelPosition == 'top' ? 'border-0' : '', $attrs.class]"
                :readonly="props.readonly"
                :disabled="props.disabled"
                :icon="props.icon"
                :label="props.label"
                :labelPosition="props.labelPosition"
                :markTextPart="props.markTextPart"
                :modelModifiers="modelModifiers"
                :maxlength="props.maxlength"
                :tooltip="props.tooltip"
                :locked="props.locked"
                :tabindex="props.tabindex"
            />
            <textarea
                v-else
                v-model="value"
                :placeholder="$tm(props.placeholder)"
                class="form-control h-100"
                :rows="10"
            />
        </div>
    </ValidatorMessage>
</template>
